import React, { useState, useEffect } from 'react';
import {
    Box,
    TableCell,
    TableRow,
    LinearProgress,
    Alert,
    Snackbar,
    Typography,
    Switch,
    IconButton,
    TextField,
    Button,
    Input,
    Popover,
    Paper,
    Stack,
    Divider,
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Add as AddIcon,
} from '@mui/icons-material';
import { AppButton } from '../../../shared/components/AppButton';
import { AppTable } from '../../../shared/components/AppTable';
import { AppTablePagination } from '../../../shared/components/AppTablePagination';
import { getBanners, uploadBanner, toggleActive, updateSortOrder, deleteBanner } from '../../../shared/services/bannerService';
import { Loader } from '../../../shared/components/Loader';
import { usePermission } from '../../../shared/contexts/PermissionContext';
import { BannerItem } from '../../../shared/types/banner';

export const Banner: React.FC = () => {
    const { hasPermission, isSuperAdmin } = usePermission();
    const [banners, setBanners] = useState<BannerItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [uploading, setUploading] = useState<boolean>(false);
    const [deleteAnchorEl, setDeleteAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [selectedBannerId, setSelectedBannerId] = useState<string | null>(null);

    useEffect(() => {
        loadBanners();
    }, [currentPage, pageSize]);

    const loadBanners = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await getBanners({
                page: currentPage + 1,
                limit: pageSize
            });

            if (response.success && response.data) {
                setBanners(response.data.items || []);
                setTotalRows(response.data.pagination?.totalItems || 0);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to load banners');
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            setUploading(true);
            setError('');
            const response = await uploadBanner(file);
            if (response.success) {
                setSuccess('Banner uploaded successfully');
                loadBanners();
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to upload banner');
        } finally {
            setUploading(false);
            // Clear input
            event.target.value = '';
        }
    };

    const handleToggleActive = async (id: string, currentStatus: boolean) => {
        const originalStatus = currentStatus;
        try {
            // Optimistic update
            setBanners(prev => prev.map(b =>
                b.id === id ? { ...b, isActive: !originalStatus } : b
            ));

            const response = await toggleActive(id);
            if (!response.success) {
                // Rollback
                setBanners(prev => prev.map(b =>
                    b.id === id ? { ...b, isActive: originalStatus } : b
                ));
                setError(response.message || 'Failed to update status');
            } else {
                setSuccess(response.message || 'Status updated');
            }
        } catch (err: any) {
            // Rollback
            setBanners(prev => prev.map(b =>
                b.id === id ? { ...b, isActive: originalStatus } : b
            ));
            setError(err.response?.data?.message || 'Failed to update status');
        }
    };

    const handleSortOrderChange = async (id: string, newOrder: string) => {
        const val = parseInt(newOrder);
        if (isNaN(val)) return;

        try {
            const response = await updateSortOrder(id, val);
            if (response.success) {
                setBanners(prev => prev.map(b =>
                    b.id === id ? { ...b, sortOrder: val } : b
                ));
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to update sort order');
        }
    };

    const handleDeleteClick = (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
        setDeleteAnchorEl(event.currentTarget);
        setSelectedBannerId(id);
    };

    const handleDeleteClose = () => {
        setDeleteAnchorEl(null);
        setSelectedBannerId(null);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedBannerId) return;

        try {
            const response = await deleteBanner(selectedBannerId);
            if (response.success) {
                setSuccess('Banner deleted successfully');
                loadBanners();
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to delete banner');
        } finally {
            handleDeleteClose();
        }
    };

    const handleCloseSnackbar = () => {
        setError('');
        setSuccess('');
    };

    const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
        setCurrentPage(newPage - 1);
    };

    const canManage = isSuperAdmin() || hasPermission('banner.manage');

    return (
        <div className="flex flex-col flex-auto min-w-0">
            {/* Header */}
            <div className="flex flex-col sm:flex-row flex-0 sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b bg-card dark:bg-transparent">
                <div className="flex-1 min-w-0">
                    <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                        Banners
                    </h2>
                </div>
                <div className="flex items-center gap-4 mt-4 sm:mt-0">
                    {canManage && (
                        <label htmlFor="banner-upload">
                            <Input
                                id="banner-upload"
                                type="file"
                                sx={{ display: 'none' }}
                                onChange={handleFileChange}
                                inputProps={{ accept: 'image/*' }}
                            />
                            <AppButton
                                variant="primary"
                                component="span"
                                startIcon={<AddIcon />}
                                loading={uploading}
                                disabled={uploading}
                            >
                                {uploading ? 'Uploading...' : 'Upload Banner'}
                            </AppButton>
                        </label>
                    )}
                </div>
            </div>

            {/* Content Area */}
            <div className="flex flex-auto p-6 sm:p-10">
                <div className="flex flex-col flex-auto min-w-0 w-full">
                    {loading && banners.length > 0 && <LinearProgress className="mb-4" />}
                    {loading && banners.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Loader />
                        </Box>
                    ) : banners.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <Alert severity="info">
                                No banners found. Upload a new banner to get started.
                            </Alert>
                        </Box>
                    ) : (
                        <>
                            <AppTable
                                head={
                                    <TableRow className="bg-gray-100">
                                        <TableCell className="!font-semibold !text-gray-700">Image</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Status</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Sort Order</TableCell>
                                        <TableCell className="!font-semibold !text-gray-700">Created At</TableCell>
                                        <TableCell align="right" className="!font-semibold !text-gray-700">Actions</TableCell>
                                    </TableRow>
                                }
                                isEmpty={banners.length === 0}
                                emptyColSpan={5}
                            >
                                {banners.map((item) => (
                                    <TableRow key={item.id} hover>
                                        <TableCell>
                                            <Box
                                                component="img"
                                                src={item.imageUrl}
                                                alt="Banner"
                                                sx={{
                                                    width: 150,
                                                    height: 80,
                                                    objectFit: 'cover',
                                                    borderRadius: 1,
                                                    border: '1px solid #eee'
                                                }}
                                                onError={(e: any) => {
                                                    e.target.src = 'https://via.placeholder.com/150x80?text=Error';
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Switch
                                                checked={item.isActive}
                                                onChange={() => handleToggleActive(item.id, item.isActive)}
                                                color="primary"
                                                size="small"
                                                disabled={!canManage}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                type="number"
                                                size="small"
                                                defaultValue={item.sortOrder}
                                                onBlur={(e) => handleSortOrderChange(item.id, e.target.value)}
                                                sx={{ width: 80 }}
                                                disabled={!canManage}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {new Date(item.createdAt).toLocaleDateString()}
                                        </TableCell>
                                        <TableCell align="right">
                                            {canManage && (
                                                <IconButton
                                                    color="error"
                                                    onClick={(e) => handleDeleteClick(e, item.id)}
                                                    size="small"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </AppTable>

                            <AppTablePagination
                                totalRows={totalRows}
                                page={currentPage}
                                pageSize={pageSize}
                                onPageChange={handlePageChange}
                                onPageSizeChange={(size) => {
                                    setPageSize(size);
                                    setCurrentPage(0);
                                }}
                            />
                        </>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Popover */}
            <Popover
                open={Boolean(deleteAnchorEl)}
                anchorEl={deleteAnchorEl}
                onClose={handleDeleteClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        p: 0,
                        mt: 1.5,
                        width: 250,
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                    },
                }}
            >
                <div className="p-4 bg-white">
                    <Typography className="font-semibold text-gray-900" sx={{ mb: 1 }}>
                        Are you sure?
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        This banner will be permanently deleted.
                    </Typography>
                    <Divider sx={{ my: 1.5 }} />
                    <div className="flex justify-end gap-2">
                        <AppButton
                            variant="secondary"
                            size="small"
                            onClick={handleDeleteClose}
                        >
                            Cancel
                        </AppButton>
                        <AppButton
                            variant="danger"
                            size="small"
                            onClick={handleDeleteConfirm}
                        >
                            Delete
                        </AppButton>
                    </div>
                </div>
            </Popover>

            <Snackbar
                open={!!error || !!success}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {error ? (
                    <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
                        {error}
                    </Alert>
                ) : (
                    <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                        {success}
                    </Alert>
                )}
            </Snackbar>
        </div>
    );
};
